import {
  SESSION_ERRORS,
  getPresenceApiBase,
  getSuiteApiBase,
  getWhoamiUrl,
  isEmbeddedInHub,
  readUserId,
  syncUserIdFromQuery,
} from "./session";

const APP_DISPLAY_NAME = "Présence";
const SUITE_APP_TILE = "Présence";

function hideRoot(): void {
  document.getElementById("root")?.classList.add("hidden");
}

function showRoot(): void {
  document.getElementById("root")?.classList.remove("hidden");
}

function hideSplash(immediate = false): void {
  const el = document.getElementById("presenceSplash");
  if (!el || el.dataset.leaving === "1") return;
  el.dataset.leaving = "1";
  const born = window.__presenceBootBorn || Date.now();
  const wait = immediate ? 0 : Math.max(0, 1900 - (Date.now() - born));
  window.setTimeout(() => el.classList.add("is-off"), wait);
}

function lockShell(): void {
  window.lockSuiteAppShell?.();
  hideRoot();
}

function unlockShell(): void {
  window.unlockSuiteAppShell?.();
  showRoot();
  hideSplash();
}

function showForbidden(): void {
  window.hideSuiteAuthModal?.();
  lockShell();
  window.showSuiteForbiddenModal?.({
    actions: [
      {
        label: "Actualiser",
        primary: true,
        onClick: () => {
          window.hideSuiteForbiddenModal?.();
          window.location.reload();
        },
      },
    ],
  });
  window.dispatchEvent(new CustomEvent("presenceAuthLost"));
  hideSplash(true);
}

function showNeedLogin(options?: { sessionLost?: boolean }): void {
  const sessionLost = options?.sessionLost === true;
  lockShell();
  window.hideSuiteForbiddenModal?.();
  window.showSuiteAuthModal?.(undefined, {
    autoReconnect: !sessionLost,
  });
  window.dispatchEvent(new CustomEvent("presenceAuthLost"));
  hideSplash(true);
}

async function fetchSsoVerify(userId: string): Promise<{ ok: boolean; forbidden?: boolean }> {
  const url = `${getSuiteApiBase()}/sso-verify.php?suite_user_id=${encodeURIComponent(userId)}&app=${encodeURIComponent(SUITE_APP_TILE)}`;
  try {
    const res = await fetch(url, { method: "GET", credentials: "include", cache: "no-store" });
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string };
    if (!res.ok || !data.success) {
      const err = data.error ?? "";
      const forbidden =
        res.status === 403 ||
        err === "app_not_permitted" ||
        err === "forbidden_app_permission" ||
        err === "admin_apps_enabled" ||
        err === "admin_apps_disabled";
      return { ok: false, forbidden };
    }
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

async function fetchPing(userId: string): Promise<{ ok: boolean; forbidden?: boolean }> {
  try {
    const res = await fetch(`${getPresenceApiBase()}/ping.php`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: { "X-User-Id": userId },
    });
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; success?: boolean; error?: string };
    const err = data.error ?? "";
    if (!res.ok || !(data.ok || data.success)) {
      const forbidden =
        res.status === 403 ||
        err === "app_not_permitted" ||
        err === "forbidden_app_permission" ||
        err === "no_active_client" ||
        err === "client_forbidden";
      return { ok: false, forbidden };
    }
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

async function verifyAndEnter(userId: string): Promise<boolean> {
  const sso = await fetchSsoVerify(userId);
  if (!sso.ok) {
    window.__presenceSessionVerified = false;
    if (sso.forbidden) {
      showForbidden();
      return false;
    }
    showNeedLogin({ sessionLost: true });
    return false;
  }
  const ping = await fetchPing(userId);
  if (!ping.ok) {
    window.__presenceSessionVerified = false;
    if (ping.forbidden) {
      showForbidden();
      return false;
    }
    showNeedLogin({ sessionLost: true });
    return false;
  }
  window.__presenceSessionVerified = true;
  unlockShell();
  window.hideSuiteAuthModal?.();
  window.hideSuiteForbiddenModal?.();
  window.startSuiteHubSessionWatch?.();
  window.dispatchEvent(new CustomEvent("presenceAuthReady", { detail: { userId } }));
  return true;
}

async function onAuthenticated(userIdArg?: number | string): Promise<void> {
  const userId = userIdArg != null ? String(userIdArg) : readUserId();
  if (!userId) return;
  if (!window.__aureliaOwnSite) window.persistSuiteUserId?.(userId);
  await verifyAndEnter(userId);
}

function handleSessionLost(): void {
  if (window.__aureliaOwnSite) {
    window.__presenceSessionVerified = false;
    showNeedLogin({ sessionLost: true });
    return;
  }
  window.__presenceSessionVerified = false;
  window.stopSuiteIframeSsoPoll?.();
  window.stopSuiteHubSessionWatch?.();
  window.clearSuiteStoredIdentity?.();
  showNeedLogin({ sessionLost: true });
}

function mountSharedModals(): void {
  window.suiteAuthModalMount?.({ appName: APP_DISPLAY_NAME });
  window.suiteForbiddenModalMount?.({ appName: APP_DISPLAY_NAME });
  window.installSuiteAppShell?.({ shellId: "presenceAppShell" });
  window.installSuiteClearCacheListener?.({ preserveKeys: [] });
}

function runSharedInit(): void {
  mountSharedModals();
  if (typeof window.installSuiteAuthPopup !== "function") {
    return;
  }
  window.installSuiteAuthPopup({
    redirect: "presence",
    isConnected: () => window.__presenceSessionVerified === true,
    onAuthenticated: (userId) => {
      void onAuthenticated(userId);
    },
    onSessionLost: () => {
      handleSessionLost();
    },
    patchFetch: false,
  });
  window.__suiteOnManualConnectClick = () => {
    if (!window.__aureliaOwnSite) return;
    void (async () => {
      const userId = await window.aureliaHub2User?.();
      if (userId) await onAuthenticated(userId);
    })();
  };
  const whoamiUrl = getWhoamiUrl();
  if (!window.__aureliaOwnSite && whoamiUrl && typeof window.installSuiteHubSessionWatch === "function") {
    window.installSuiteHubSessionWatch({
      whoamiUrl,
      isEmbeddedInHub,
      hasStoredIdentity: () => Boolean(readUserId()),
      isAppSessionActive: () => window.__presenceSessionVerified === true,
      onSessionLost: () => {
        handleSessionLost();
      },
    });
  }
  void bootAuth();
}

async function bootAuth(): Promise<void> {
  if (window.__aureliaOwnSite) {
    lockShell();
    const userId = await window.aureliaHub2User?.();
    if (!userId) {
      showNeedLogin({ sessionLost: false });
      return;
    }
    await verifyAndEnter(userId);
    return;
  }
  syncUserIdFromQuery();
  lockShell();
  const delay = isEmbeddedInHub() ? 2600 : 800;
  await new Promise((r) => setTimeout(r, delay));
  const userId = readUserId();
  if (userId) {
    await verifyAndEnter(userId);
    return;
  }
  showNeedLogin({ sessionLost: false });
}

export function presenceNotifyApiAuthError(status: number, error?: string): boolean {
  const err = error ?? "";
  if (status === 403 && (err === "app_not_permitted" || err === "forbidden_app_permission" || err === "no_active_client")) {
    showForbidden();
    return true;
  }
  if (status === 401 && SESSION_ERRORS.has(err)) {
    handleSessionLost();
    return true;
  }
  return false;
}

if (window.__SUITE_SHARED_READY__) {
  runSharedInit();
} else {
  document.addEventListener("suiteSharedReady", runSharedInit, { once: true });
}
